<template>
  <div v-if="!isHidden" class="qoder-message" :class="`qoder-message--${blockType}`">
    <thinking-block
      v-if="blockType === QODER_BLOCK_TYPES.THINKING"
      :has-unfinished-session="hasUnfinishedSession"
      :is-last="isStreaming"
      :text="message.text"
    />
    <div
      v-else-if="blockType === QODER_BLOCK_TYPES.TOOL_USE"
      class="qoder-message__tool-group"
      :class="{ 'qoder-message__tool-group--clickable': isToolGroupCollapsible }"
      @click="handleToolUseClick"
    >
      <tool-use-block
        :all-items="allItems"
        :has-unfinished-session="hasUnfinishedSession"
        :is-collapsed="isCollapsed"
        :is-last="isStreaming"
        :is-tool-group-collapsible="isToolGroupCollapsible"
        :text="message.text"
        :tool-name="message.toolName"
      />
      <i
        v-if="isToolGroupCollapsible"
        class="icon-arrow-right-s-line qoder-message__collapse-arrow t-iconfont"
        :class="{ 'is-expanded': !isCollapsed }"
      ></i>
    </div>
    <error-block v-else-if="blockType === QODER_BLOCK_TYPES.ERROR" :text="message.text" />
    <artifact-card
      v-else-if="blockType === QODER_BLOCK_TYPES.ARTIFACT"
      class="qoder-message__artifact"
      :content-type="message.contentType"
      :is-json="message.isJson"
      :original-filename="message.originalFilename"
      :processed-at="message.processedAt"
      @click="handleArtifactClick"
      @download="handleArtifactDownload"
    />
    <template v-else>
      <markdown-view
        v-if="message.text"
        class="qoder-message__message"
        :html-options="false"
        :show-string="message.text"
      ></markdown-view>
      <div v-else-if="isStreaming" class="qoder-message__pending">
        <span class="qoder-message__dot"></span>
        <span class="qoder-message__dot"></span>
        <span class="qoder-message__dot"></span>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import MarkdownView from '@/components/markdown-view/index.vue'

import { QODER_BLOCK_TYPES } from '../../use-qoder-cloud-agent'

import ArtifactCard from './components/artifact-card.vue'
import ErrorBlock from './components/error-block.vue'
import ThinkingBlock from './components/thinking-block.vue'
import ToolUseBlock from './components/tool-use-block.vue'

defineOptions({
  name: 'QoderMessage'
})

const props = defineProps({
  message: {
    type: Object,
    default: () => ({})
  },
  hasUnfinishedSession: {
    type: Boolean,
    default: false
  },
  // 组首且下方还有相邻的 tool_use：可点击收起，并展示折叠箭头。
  isToolGroupCollapsible: {
    type: Boolean,
    default: false
  },
  // 所有 tool_use 组成的分组：{ items: [...] }，首项为组首。
  allItems: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['artifact-click', 'artifact-download', 'tool-use-toggle'])

// 一条消息只渲染一种类型的数据，缺省按纯文本处理。
const blockType = computed(() => props.message.blockType || QODER_BLOCK_TYPES.MESSAGE)

// 还在流式输出：用于展示等待动画与状态块的进行态。
const isStreaming = computed(() => Boolean(props.message.streaming))

// 被所属 tool_use 组收起时整条不渲染；展开/收起动画由外层 tool-use-group 统一执行。
const isHidden = computed(() => Boolean(props.message.collapsed))

// 组首记录的整组收起态：为真时箭头朝右。
const isCollapsed = computed(() => Boolean(props.message.groupCollapsed))

const handleArtifactClick = () => {
  emit('artifact-click', props.message)
}

/**
 * 通过临时 <a download> 触发浏览器下载。
 * 跨域链接的 download 属性会被忽略，因此走链接下载时开新标签，避免当前页面被替换。
 */
const triggerDownload = (href, fileName, openInNewTab = false) => {
  const link = document.createElement('a')
  link.href = href
  link.download = fileName
  if (openInNewTab) {
    link.target = '_blank'
    link.rel = 'noopener'
  }
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/**
 * 下载链接指向的文件：优先 fetch 成 blob 后本地保存，保证文件名可控；
 * 跨域或不支持 CORS 时退化为直接用链接下载（此时文件名以服务端返回为准）。
 * @param {string} fileUrl 文件下载链接
 * @param {string} filename 期望的文件名
 * @returns {Promise<boolean>} 是否已发起下载
 */
const downloadFile = async (fileUrl, filename) => {
  if (!fileUrl) return false

  // 过滤非法字符，保证文件名可安全落盘。
  const fileName =
    String(filename || '')
      .trim()
      .replace(/[\\/:*?"<>|]/g, '_')
      .slice(0, 100) || '文件'

  try {
    const res = await fetch(fileUrl, { credentials: 'omit' })
    if (!res.ok) throw new Error(`download failed with status ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    triggerDownload(objectUrl, fileName)
    // 立即 revoke 可能导致部分浏览器下载中断，延后释放
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    return true
  } catch (error) {
    console.error('[artifact] download failed', error)
    triggerDownload(fileUrl, fileName, true)
    return true
  }
}

/**
 * 文件投递物触发下载：下载动作包成回调一起交给顶层，
 * 顶层按现有方式取到下载链接后回调触发，本组件不感知取链细节。
 */
const handleArtifactDownload = () => {
  emit('artifact-download', props.message, (fileUrl) =>
    downloadFile(fileUrl, props.message.originalFilename)
  )
}

/** 收起/展开下方相邻的 tool_use，具体范围由父级按消息顺序计算。 */
const handleToolUseClick = () => {
  if (!props.isToolGroupCollapsible) return
  emit('tool-use-toggle', props.message)
}
</script>

<style lang="scss" scoped>
.qoder-message {
  min-width: 0;

  // 思考与工具调用是单行状态，紧凑排列更接近连续的过程流。
  &--artifact {
    margin-top: 8px;
    margin-bottom: 8px;
  }

  &--thinking {
    padding: 0;
  }
  &--tool_use {
    padding: 4px 0;
    // border-left: 4px solid #e8e8e8;
    // padding-left: 16px;
  }

  &--message {
    margin-top: 12px;
  }
  &__pending {
    display: flex;
    align-items: center;
    height: 22px;
  }

  &__tool-group {
    display: flex;
    align-items: center;
    min-width: 0;

    // 宽度按内容收缩：文字短时箭头紧贴其后，文字过长时才被压缩出省略号并把箭头顶到最右侧。
    > .tool-use-block {
      flex: 0 1 auto;
      min-width: 0;
    }

    &--clickable {
      cursor: pointer;
    }
  }

  // 折叠箭头：收起态朝右，展开态向下旋转 90 度。
  &__collapse-arrow {
    flex: none;
    margin-left: 4px;
    color: #858e9f;
    font-size: 16px;
    line-height: 22px;
    transition: transform 0.24s ease;

    &.is-expanded {
      transform: rotate(90deg);
    }
  }

  &__dot {
    width: 6px;
    height: 6px;
    margin-right: 4px;
    border-radius: 50%;
    background: #c9cdd4;
    animation: qoder-message-blink 1.2s infinite ease-in-out both;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      margin-right: 0;
      animation-delay: 0.4s;
    }
  }
}

@keyframes qoder-message-blink {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }

  40% {
    opacity: 1;
  }
}

@keyframes qoder-message-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
